import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalChangePassword } from '../../services/portalApi';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  pending_assistance: 'bg-orange-100 text-orange-700',
  error: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {t(`portal.statuses.${status}`, { defaultValue: status.replace(/_/g, ' ') })}
    </span>
  );
};

export const ChangePasswordPage: React.FC = () => {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError(t('portal.changePassword.passwordMismatch')); return; }
    if (next.length < 8) { setError(t('portal.changePassword.passwordTooShort')); return; }
    setLoading(true);
    try {
      await portalChangePassword(current, next);
      navigate(`/portal/${orgAlias}/orders`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('portal.changePassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  const fields: [string, string, React.Dispatch<React.SetStateAction<string>>][] = [
    [t('portal.changePassword.currentPassword'), current, setCurrent],
    [t('portal.changePassword.newPassword'), next, setNext],
    [t('portal.changePassword.confirmPassword'), confirm, setConfirm],
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1">{t('portal.changePassword.title')}</h2>
        <p className="text-sm text-gray-500 mb-6">{t('portal.changePassword.subtitle')}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(([label, val, setter]) => (
            <div key={label}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <input type="password" required value={val} onChange={(e) => setter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
            </div>
          ))}
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}
          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60 text-sm">
            {loading ? t('portal.changePassword.saving') : t('portal.changePassword.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};
